import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Edit, Trash2, DollarSign, Star, Clock, CheckCircle, X, AlertCircle } from "lucide-react";

const SubscriptionPlansManagement = ({ API, token }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPlan, setEditingPlan] = useState(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    display_name: "",
    description: "",
    price: 0,
    currency: "INR",
    duration_days: 30,
    duration_type: "monthly",
    features: [],
    accessible_modules: [],
    limits: {},
    is_featured: false,
    display_order: 0
  });
  const [featureInput, setFeatureInput] = useState("");
  const [moduleInput, setModuleInput] = useState("");

  // Comprehensive list of ALL available USER modules in SkillEdge platform
  // Note: Admin features are excluded as they should only be accessible via admin role
  const availableModules = [
    // Core Features
    "Dashboard",
    "Profile Management",
    "Pre-Assessment",
    
    // Career & Assessment
    "Career Assessment",
    "Career Library",
    "Career Comparison",
    "Career Detail Pages",
    "Mock Interviews",
    
    // AI & Consultation
    "AI Chatbot",
    "AI Consultation",
    "Live Consultations",
    "Consultation Booking",
    "My Consultations",
    
    // Learning & Education
    "Courses",
    "Course Enrollment",
    "Study Planner",
    "Quizzes",
    "Exams",
    "Resources",
    
    // Institutions & Information
    "Top Colleges",
    "College Details",
    "Scholarships",
    "Events",
    
    // Career Opportunities
    "Opportunities",
    "Job Search",
    "Internship Search",
    "My Applications",
    "Saved Opportunities",
    
    // Community Features
    "Community Hub",
    "Discussion Forums",
    "Peer Mentoring",
    "Mentors Directory",
    "Success Stories",
    "Study Groups",
    "Q&A Section",
    
    // Progress & Achievements
    "Progress Tracking",
    "Progress Dashboard",
    "Leaderboard",
    "Certificates",
    "Badges & Rewards",
    
    // Communication & Support
    "Notifications",
    "Support Center"
  ];

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/admin/subscription-plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Ensure response.data is an array and normalize plan data
      const plansData = Array.isArray(response.data) ? response.data : [];
      
      // Normalize each plan to ensure all array fields exist
      const normalizedPlans = plansData.map(plan => ({
        ...plan,
        features: Array.isArray(plan.features) ? plan.features : [],
        accessible_modules: Array.isArray(plan.accessible_modules) ? plan.accessible_modules : [],
        limits: plan.limits || {}
      }));
      
      setPlans(normalizedPlans);
    } catch (error) {
      console.error("Error fetching plans:", error);
      toast.error("Failed to fetch subscription plans");
      setPlans([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      display_name: "",
      description: "",
      price: 0,
      currency: "INR",
      duration_days: 30,
      duration_type: "monthly",
      features: [],
      accessible_modules: [],
      limits: {},
      is_featured: false,
      display_order: 0
    });
    setFeatureInput("");
    setModuleInput("");
    setEditingPlan(null);
  };

  const handleCreate = async () => {
    try {
      // Validation
      if (!formData.display_name.trim()) {
        toast.error("Please enter a plan display name");
        return;
      }
      if (!formData.name.trim()) {
        toast.error("Plan identifier cannot be empty");
        return;
      }
      if (formData.price < 0) {
        toast.error("Price cannot be negative");
        return;
      }

      console.log("Creating plan with data:", formData);

      await axios.post(`${API}/admin/subscription-plans`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Subscription plan created successfully");
      fetchPlans();
      setIsCreateDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error("Error creating plan:", error);
      console.error("Error response:", error.response?.data);
      
      // Handle different error formats
      let errorMessage = "Failed to create plan";
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // FastAPI validation error format
        if (Array.isArray(errorData.detail)) {
          const errors = errorData.detail.map(err => `${err.loc.join('.')}: ${err.msg}`).join(', ');
          errorMessage = `Validation error: ${errors}`;
        } 
        // Simple string error
        else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        }
        // Object error
        else if (errorData.detail && typeof errorData.detail === 'object') {
          errorMessage = JSON.stringify(errorData.detail);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  };

  const handleUpdate = async () => {
    try {
      const updateData = { ...formData };
      delete updateData.name; // Don't update name
      
      console.log("Updating plan with data:", updateData);
      
      await axios.put(`${API}/admin/subscription-plans/${editingPlan.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Subscription plan updated successfully");
      fetchPlans();
      setEditingPlan(null);
      resetForm();
    } catch (error) {
      console.error("Error updating plan:", error);
      console.error("Error response:", error.response?.data);
      
      // Handle different error formats
      let errorMessage = "Failed to update plan";
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // FastAPI validation error format
        if (Array.isArray(errorData.detail)) {
          const errors = errorData.detail.map(err => `${err.loc.join('.')}: ${err.msg}`).join(', ');
          errorMessage = `Validation error: ${errors}`;
        } 
        // Simple string error
        else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        }
        // Object error
        else if (errorData.detail && typeof errorData.detail === 'object') {
          errorMessage = JSON.stringify(errorData.detail);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  };

  const handleDelete = async (planId) => {
    if (!window.confirm("Are you sure you want to delete this plan?")) return;
    
    try {
      await axios.delete(`${API}/admin/subscription-plans/${planId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Subscription plan deleted successfully");
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete plan");
    }
  };

  const addFeature = () => {
    if (featureInput.trim()) {
      setFormData({
        ...formData,
        features: [...formData.features, featureInput.trim()]
      });
      setFeatureInput("");
    }
  };

  const removeFeature = (index) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== index)
    });
  };

  const toggleModule = (module) => {
    const modules = formData.accessible_modules.includes(module)
      ? formData.accessible_modules.filter(m => m !== module)
      : [...formData.accessible_modules, module];
    setFormData({ ...formData, accessible_modules: modules });
  };

  if (loading) {
    return <div className="text-center py-8">Loading subscription plans...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold">Subscription Plans Management</h3>
          <p className="text-gray-600">Create and manage subscription plans for your users</p>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Create Plan
            </Button>
          </DialogTrigger>
          <PlanDialogContent 
            isEdit={false}
            formData={formData}
            setFormData={setFormData}
            featureInput={featureInput}
            setFeatureInput={setFeatureInput}
            addFeature={addFeature}
            removeFeature={removeFeature}
            toggleModule={toggleModule}
            availableModules={availableModules}
            onSave={handleCreate}
            onCancel={() => {
              setIsCreateDialogOpen(false);
              resetForm();
            }}
          />
        </Dialog>
      </div>

      {plans.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <DollarSign className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h4 className="text-lg font-semibold mb-2">No Subscription Plans</h4>
            <p className="text-gray-600 mb-4">Create your first subscription plan to get started</p>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Plan
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <Card key={plan.id} className={plan.is_featured ? "border-2 border-blue-500" : ""}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CardTitle>{plan.display_name}</CardTitle>
                    {plan.is_featured && (
                      <Badge className="bg-yellow-500">
                        <Star className="w-3 h-3 mr-1" />
                        Featured
                      </Badge>
                    )}
                  </div>
                  <Badge variant={plan.is_active ? "success" : "secondary"}>
                    {plan.is_active ? "Active" : "Inactive"}
                  </Badge>
                </div>
                <CardDescription>{plan.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold">
                        {plan.currency === "INR" ? "₹" : plan.currency === "USD" ? "$" : "€"}
                        {plan.price}
                      </span>
                      <span className="text-gray-600">/{plan.duration_type}</span>
                    </div>
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <Clock className="w-4 h-4" />
                      {plan.duration_days} days
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-semibold mb-2">Features:</p>
                    <ul className="space-y-1">
                      {(plan.features || []).slice(0, 4).map((feature, index) => (
                        <li key={index} className="text-sm flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                      {(plan.features || []).length > 4 && (
                        <li className="text-sm text-gray-600">
                          +{(plan.features || []).length - 4} more features
                        </li>
                      )}
                    </ul>
                  </div>

                  <div>
                    <p className="text-sm font-semibold mb-2">Accessible Modules:</p>
                    <div className="flex flex-wrap gap-1">
                      {(plan.accessible_modules || []).slice(0, 3).map((module) => (
                        <Badge key={module} variant="outline" className="text-xs">
                          {module}
                        </Badge>
                      ))}
                      {(plan.accessible_modules || []).length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{(plan.accessible_modules || []).length - 3}
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2 pt-4 border-t">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button
                          variant="outline"
                          className="flex-1"
                          onClick={() => {
                            setEditingPlan(plan);
                            setFormData({
                              name: plan.name,
                              display_name: plan.display_name,
                              description: plan.description || "",
                              price: plan.price,
                              currency: plan.currency,
                              duration_days: plan.duration_days,
                              duration_type: plan.duration_type,
                              features: plan.features,
                              accessible_modules: plan.accessible_modules,
                              limits: plan.limits || {},
                              is_featured: plan.is_featured,
                              display_order: plan.display_order
                            });
                          }}
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </Button>
                      </DialogTrigger>
                      <PlanDialogContent 
                        isEdit={true}
                        formData={formData}
                        setFormData={setFormData}
                        featureInput={featureInput}
                        setFeatureInput={setFeatureInput}
                        addFeature={addFeature}
                        removeFeature={removeFeature}
                        toggleModule={toggleModule}
                        availableModules={availableModules}
                        onSave={handleUpdate}
                        onCancel={() => {
                          setEditingPlan(null);
                          resetForm();
                        }}
                      />
                    </Dialog>
                    <Button
                      variant="destructive"
                      onClick={() => handleDelete(plan.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// Separate component to prevent re-creation on every render
const PlanDialogContent = ({ 
  isEdit, 
  formData, 
  setFormData, 
  featureInput, 
  setFeatureInput,
  addFeature,
  removeFeature,
  toggleModule,
  availableModules,
  onSave,
  onCancel
}) => (
    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{isEdit ? "Edit" : "Create"} Subscription Plan</DialogTitle>
        <DialogDescription>
          {isEdit ? "Update" : "Create a new"} subscription plan with pricing and features
        </DialogDescription>
      </DialogHeader>

      {!isEdit && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-blue-800">
              <p className="font-semibold mb-1">Quick Guide:</p>
              <ul className="space-y-0.5 text-xs">
                <li>• <strong>Display Name:</strong> What users see (e.g., "Premium Plan")</li>
                <li>• <strong>Internal ID:</strong> Auto-generated, used in code (e.g., "premium_plan")</li>
                <li>• <strong>Features:</strong> Add user-facing benefits one by one</li>
                <li>• <strong>Modules:</strong> Select which app sections users can access</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="display_name">Plan Display Name *</Label>
            <Input
              id="display_name"
              placeholder="e.g., Basic Plan, Premium Plan, Enterprise"
              value={formData.display_name}
              onChange={(e) => {
                const displayName = e.target.value;
                setFormData({
                  ...formData, 
                  display_name: displayName,
                  // Auto-generate URL-safe name if not editing
                  name: isEdit ? formData.name : displayName.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
                });
              }}
            />
            <p className="text-xs text-gray-500 mt-1">This name will be shown to users</p>
          </div>
          <div>
            <Label htmlFor="name">
              Internal Identifier 
              <span className="text-xs text-gray-500 ml-1">(Auto-generated)</span>
            </Label>
            <Input
              id="name"
              placeholder="basic_plan"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')})}
              disabled={isEdit}
              className="bg-gray-50"
            />
            <p className="text-xs text-gray-500 mt-1">
              {isEdit ? "Cannot be changed after creation" : "Auto-fills from display name"}
            </p>
          </div>
        </div>

        <div>
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            placeholder="Brief description of the plan"
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            rows={3}
          />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label htmlFor="price">Price</Label>
            <Input
              id="price"
              type="number"
              step="0.01"
              value={formData.price}
              onChange={(e) => setFormData({...formData, price: parseFloat(e.target.value)})}
            />
          </div>
          <div>
            <Label htmlFor="currency">Currency</Label>
            <Select
              value={formData.currency}
              onValueChange={(value) => setFormData({...formData, currency: value})}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="INR">INR (₹)</SelectItem>
                <SelectItem value="USD">USD ($)</SelectItem>
                <SelectItem value="EUR">EUR (€)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="display_order">Display Order</Label>
            <Input
              id="display_order"
              type="number"
              value={formData.display_order}
              onChange={(e) => setFormData({...formData, display_order: parseInt(e.target.value)})}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="duration_type">Duration Type</Label>
            <Select
              value={formData.duration_type}
              onValueChange={(value) => {
                const days = value === "monthly" ? 30 : value === "yearly" ? 365 : 36500;
                setFormData({...formData, duration_type: value, duration_days: days});
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="yearly">Yearly</SelectItem>
                <SelectItem value="lifetime">Lifetime</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="duration_days">Duration (Days)</Label>
            <Input
              id="duration_days"
              type="number"
              value={formData.duration_days}
              onChange={(e) => setFormData({...formData, duration_days: parseInt(e.target.value)})}
            />
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch
              checked={formData.is_featured}
              onCheckedChange={(checked) => setFormData({...formData, is_featured: checked})}
            />
            <Label>Featured Plan</Label>
          </div>
        </div>

        <div>
          <Label>Features</Label>
          <div className="flex gap-2 mb-2">
            <Input
              placeholder="Add a feature (e.g., Unlimited assessments)"
              value={featureInput}
              onChange={(e) => setFeatureInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addFeature()}
            />
            <Button type="button" onClick={addFeature}>
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <div className="space-y-1">
            {formData.features.map((feature, index) => (
              <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                <span className="text-sm">{feature}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeFeature(index)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label>Accessible Modules ({formData.accessible_modules.length} selected)</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setFormData({ ...formData, accessible_modules: [...availableModules] })}
              >
                Select All
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setFormData({ ...formData, accessible_modules: [] })}
              >
                Deselect All
              </Button>
            </div>
          </div>
          <div className="border rounded-md p-3 max-h-[400px] overflow-y-auto bg-gray-50">
            <div className="grid grid-cols-2 gap-2">
              {availableModules.map((module) => (
                <div
                  key={module}
                  className={`flex items-center gap-2 p-2 border rounded cursor-pointer transition-colors ${
                    formData.accessible_modules.includes(module)
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                  onClick={() => toggleModule(module)}
                >
                  <div className={`w-4 h-4 border-2 rounded flex items-center justify-center ${
                    formData.accessible_modules.includes(module)
                      ? 'border-blue-500 bg-blue-500'
                      : 'border-gray-300'
                  }`}>
                    {formData.accessible_modules.includes(module) && (
                      <CheckCircle className="w-3 h-3 text-white" />
                    )}
                  </div>
                  <span className="text-sm">{module}</span>
                </div>
              ))}
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Select modules that will be accessible to users with this subscription plan.
          </p>
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={onSave}>
          {isEdit ? "Update" : "Create"} Plan
        </Button>
      </DialogFooter>
    </DialogContent>
);

export default SubscriptionPlansManagement;
